import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { PageViewTracker } from './analytics/PageViewTracker.js'
import { RequireAuth } from './auth/RequireAuth.js'
import { AskPage } from './routes/AskPage.js'
import { AskResultPage } from './routes/AskResultPage.js'
import { HealthPage } from './routes/HealthPage.js'
import { HomePage } from './routes/HomePage.js'
import { KitchenSinkPage } from './routes/KitchenSinkPage.js'
import { LoginPage } from './routes/LoginPage.js'
import { MutateRunPage } from './routes/MutateRunPage.js'
import { CreateTenantPage } from './routes/CreateTenantPage.js'
import { DemoUsagePage } from './routes/DemoUsagePage.js'
import { OnboardingQueuePage } from './routes/OnboardingQueuePage.js'
import { ResetPasswordPage } from './routes/ResetPasswordPage.js'
import { ReviewPage } from './routes/ReviewPage.js'
import { RfqSourceInvitesPage } from './routes/RfqSourceInvitesPage.js'
import { RunDetailPage } from './routes/RunDetailPage.js'
import { RulesPage } from './routes/RulesPage.js'
import { RunsPage } from './routes/RunsPage.js'
import { AcceptTenantInvitePage } from './routes/AcceptTenantInvitePage.js'
import { CustomerCommercialRulesPage } from './routes/CustomerCommercialRulesPage.js'
import { CustomerPriceListsPage } from './routes/CustomerPriceListsPage.js'
import { CustomerTeamPage } from './routes/CustomerTeamPage.js'
import { TenantLoginPage } from './routes/TenantLoginPage.js'
import { RequireTenantAuth } from './tenant-auth/RequireTenantAuth.js'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // The tech stack spec's own default is `true`; overridden here per
      // the UI spec's opening principle — no implicit "live" claims,
      // refresh is manual and obvious. Tabbing back to the console must not
      // silently refetch behind the operator's back. `staleTime` still
      // governs when a *newly mounted* query is considered fresh; this only
      // stops an already-mounted one from refetching on focus.
      refetchOnWindowFocus: false,
      staleTime: 60_000,
      /**
       * `REQ-design-the-operator-console-ux-audit`. React Query's own
       * default — three retries, exponential backoff — was never chosen on
       * purpose here; it was just what's left when nothing overrides it.
       * `18UX`'s own audit found the same root cause behind three separate
       * findings across two passes: a genuine 404 (a run with nothing to
       * review, an unreachable `customer-app`) still takes several seconds
       * and several failed attempts before the screen can say so. One retry,
       * one short delay — matches `tenant-auth/queries.ts`'s own `useTenantMe`
       * override (`S1`, `LGH-047`), now the app-wide default rather than a
       * one-query fix. A query that genuinely needs more resilience against a
       * flaky real Cadence can still override this per-query, the same way
       * `ReviewPage`'s own `staleTime: 0` already does for freshness.
       */
      retry: 1,
      retryDelay: 1_000,
    },
  },
})

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <PageViewTracker />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          {/* Dev-only — `import.meta.env.DEV` is false in a production build,
              so this route is never registered outside local development. */}
          {import.meta.env.DEV && <Route path="/kitchen-sink" element={<KitchenSinkPage />} />}
          <Route element={<RequireAuth />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/runs" element={<RunsPage />} />
            <Route path="/runs/:id" element={<RunDetailPage />} />
            {/* A mode of a run, like approve/refuse/rule — LGH-007 Q4. */}
            <Route path="/runs/:id/review" element={<ReviewPage />} />
            <Route path="/runs/:id/approve" element={<MutateRunPage action="approve" />} />
            <Route path="/runs/:id/refuse" element={<MutateRunPage action="refuse" />} />
            <Route path="/runs/:id/rule" element={<MutateRunPage action="rule" />} />
            <Route path="/ask" element={<AskPage />} />
            <Route path="/ask/result/:id" element={<AskResultPage />} />
            <Route path="/rules" element={<RulesPage />} />
            <Route path="/health" element={<HealthPage />} />
            <Route path="/rfq-source-invites" element={<RfqSourceInvitesPage />} />
            <Route path="/onboarding-requests" element={<OnboardingQueuePage />} />
            <Route path="/demo-usage" element={<DemoUsagePage />} />
            <Route path="/tenants/new" element={<CreateTenantPage />} />
          </Route>

          {/*
            `LGH-040`. The customer console — a separate session
            (`pruvato_tenant_session`, `customer-app` directly per `DR-033`),
            never the operator's `RequireAuth`. Deliberately its own
            sub-tree rather than interleaved with the operator routes above,
            so the two session models can never be confused for one route
            table.
          */}
          <Route path="/customer/login" element={<TenantLoginPage />} />
          <Route path="/customer/accept-invite/:inviteId" element={<AcceptTenantInvitePage />} />
          <Route element={<RequireTenantAuth />}>
            <Route path="/customer/team" element={<CustomerTeamPage />} />
            <Route path="/customer/commercial-rules" element={<CustomerCommercialRulesPage />} />
            <Route path="/customer/price-lists" element={<CustomerPriceListsPage />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
